// What the jump bar lists: rows, each a place and what can be done there.
// A row's actions say what each choice does ("Review against main",
// "History", its pull request), the first being what Enter takes. Every
// address an action goes to is the router's own (`prPath`, `comparePath`,
// `historyPath`, `subjectPath`): no row has its own idea of a URL.
import { comparePath, historyPath, prPath, repoPath as routerRepoPath, subjectPath } from '../router';
import { compareHead, subjectKey, type Subject } from '../subject';

/** A subject or repository kept in the jump bar's recents or pins: its key, and the title and detail its row showed. */
export interface Place {
  key: string;
  title?: string;
  detail?: string;
}

export type Action =
  /** Go to `href`; `remember` is the subject it opens, kept as a recent with its title. */
  | { kind: 'go'; label: string; href: string; remember?: Place }
  /** Pin the subject, or unpin it (the bar says which, from the pins it holds). */
  | { kind: 'pin'; place: Place };

export interface Row {
  /** Who the row is, across every source: a subject's key, `branch:o/r:name`, `repo:o/r`. */
  id: string;
  /** Other ids it stands for: a branch with an open pull request is that pull request (its id) and the branch. */
  aliases?: readonly string[];
  title: string;
  detail?: string;
  /** At least one; the first is what Enter takes. */
  actions: readonly Action[];
  /** Read from the text alone: a row a source knows more of, with an id it stands for, takes its place. */
  provisional?: true;
}

export interface Repo {
  owner: string;
  repo: string;
}

const lower = (r: Repo) => `${r.owner}/${r.repo}`.toLowerCase();

/** A branch row's id, which a branch with a pull request also stands for. */
export const branchId = (r: Repo, name: string) => `branch:${lower(r)}:${name}`;

/** The repository's page (`/gh/o/r`), on its Pull requests tab. */
export const repoPath = (r: Repo) => routerRepoPath(r.owner, r.repo);

/** A repository's id among rows and pins: `repo:owner/repo`, lowercased (GitHub's names are case-insensitive). */
export const repoId = (r: Repo) => `repo:${lower(r)}`;

/** A repository's row: its page, then pinning it. */
export function repoRow(r: Repo, o: { detail?: string } = {}): Row {
  const id = repoId(r);
  const title = `${r.owner}/${r.repo}`;
  return { id, title, ...(o.detail ? { detail: o.detail } : {}), actions: [{ kind: 'go', label: 'Pull requests', href: repoPath(r) }, { kind: 'pin', place: { key: id, title } }] };
}

const SHA_LIKE = /^[0-9a-fA-F]{7,40}$/;

/** What opening a subject is called on its row. */
const OPEN: Readonly<Record<Subject['kind'], string>> = { pull: 'Open pull request', compare: 'Review', ref: 'History' };

/**
 * A subject's row: opening it (at `href`, a URL that names one of its
 * views, or its own path; a history's names where its ref ends,
 * `subjectPath`, `?under=`), then pinning it. A pull request's `title` is GitHub's,
 * when known; `detail` says more than its repository (its head branch).
 */
export function subjectRow(subject: Subject, o: { title?: string; detail?: string; href?: string } = {}): Row {
  const key = subjectKey(subject);
  const repo = `${subject.owner}/${subject.repo}`.toLowerCase();
  const place: Place = { key, ...(o.title ? { title: o.title } : {}), ...(o.detail ? { detail: o.detail } : {}) };
  const title =
    subject.kind === 'pull'
      ? `#${subject.number}${o.title ? ' ' + o.title : ''}`
      : subject.kind === 'compare'
        ? `${subject.base === null ? '' : subject.base + '...'}${compareHead(subject)}`
        : `${subject.ref}${subject.path === null ? '' : ' · ' + subject.path}`;
  return {
    id: key,
    title,
    detail: o.detail ?? repo,
    actions: [
      { kind: 'go', label: OPEN[subject.kind], href: o.href ?? subjectPath(subject), remember: place },
      { kind: 'pin', place },
    ],
  };
}

/** A branch's open pull request, as a row names it. */
export interface BranchPull {
  number: number;
  title: string;
}

/**
 * A branch's row. A branch with an open pull request is that pull request
 * (its id, number and title), opened first, and the branch besides; then
 * reviewing it against the default branch (not on the default branch
 * itself), and its history. `defaultBranch` null: not known, and the
 * comparison is with whatever the default branch is.
 */
export function branchRow(r: Repo, name: string, o: { defaultBranch: string | null; pull: BranchPull | null }): Row {
  const actions: Action[] = [];
  let pullPlace: Place | null = null;
  if (o.pull) {
    pullPlace = { key: subjectKey({ kind: 'pull', owner: r.owner, repo: r.repo, number: o.pull.number }), title: o.pull.title, detail: `${`${r.owner}/${r.repo}`.toLowerCase()} · ${name}` };
    actions.push({ kind: 'go', label: 'Open pull request', href: prPath(r.owner, r.repo, o.pull.number), remember: pullPlace });
  }
  if (name !== o.defaultBranch) {
    const review = comparePath(r.owner, r.repo, o.defaultBranch ?? '', name);
    if (review) actions.push({ kind: 'go', label: o.defaultBranch ? `Review against ${o.defaultBranch}` : 'Review against the default branch', href: review });
  }
  const history = historyPath(r.owner, r.repo, name);
  if (history) actions.push({ kind: 'go', label: 'History', href: history });
  if (pullPlace) actions.push({ kind: 'pin', place: pullPlace });
  const id = branchId(r, name);
  const detail = o.pull ? name : name === o.defaultBranch ? 'default branch' : SHA_LIKE.test(name) ? 'commit' : undefined;
  return {
    id: pullPlace ? pullPlace.key : id,
    ...(pullPlace ? { aliases: [id] } : {}),
    title: o.pull ? `#${o.pull.number} ${o.pull.title}` : name,
    ...(detail ? { detail } : {}),
    actions,
  };
}

/**
 * How well `text` matches a row's `fields`: 3 when one starts with it, 2
 * when a word of one does (after `/`, `-`, `_`, `.`, `#`, `:` or a space),
 * 1 when one holds it, 0 when none does; ignoring case. Every row matches
 * an empty text.
 */
export function matchScore(text: string, fields: readonly (string | null | undefined)[]): number {
  const q = text.trim().toLowerCase();
  if (!q) return 1;
  let best = 0;
  for (const f of fields) {
    if (!f) continue;
    const s = f.toLowerCase();
    const at = s.indexOf(q);
    if (at < 0) continue;
    if (at === 0) return 3;
    best = Math.max(best, /[\s/\-_.#:]/.test(s[at - 1]) || s.split(/[\s/\-_.#:]/).some((w) => w.startsWith(q)) ? 2 : 1);
  }
  return best;
}

/** The items that match `text` best first (and in their order among equals), with those that do not left out. */
export function ranked<T>(text: string, items: readonly T[], fields: (item: T) => readonly (string | null | undefined)[]): T[] {
  return items
    .map((item, i) => ({ item, i, score: matchScore(text, fields(item)) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.item);
}
